import { ClassType, EntityType, useAppSelector } from '@frontend/common';
import { ErrorHandler } from '@frontend/error-handler';
import { FunctionalityClient } from '@frontend/functionality/api';
import { Application, Feature, FeatureTarget } from '@frontend/functionality/types';
import { UserGroupClient } from '@frontend/group/api';
import { IoTClient } from '@frontend/iot/api';
import { IoTStateName } from '@frontend/iot/types';
import { useDocumentRepository } from '@frontend/repository';
import { SettingClient } from '@frontend/setting/api';
import { Setting, SettingKey } from '@frontend/setting/types';
import { SlotClient, SlotWorkflowClient } from '@frontend/slot/api';
import { Slot, SlotType } from '@frontend/slot/types';
import { SpotClient } from '@frontend/spot/api';
import { Spot } from '@frontend/spot/types';
import { navigationStore, updateIoTState } from '@frontend/terminal/shared';
import { TransactionPuDoClient, TransactionWorkflowClient } from '@frontend/transaction/api';
import { ExtendedPudoTransaction, TransactionTriggerName, TransactionType } from '@frontend/transaction/types';
import { UserClient } from '@frontend/user/api';
import { IUser } from '@frontend/user/types';
import { Dispatch, SetStateAction, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';

import { UserInterfaceProps } from '../types/user-interface';

export interface ViewProps {
    senders: UserWithDocument[];
    selectedSender: UserWithDocument | null;
    setSelectedSender: Dispatch<SetStateAction<UserWithDocument | null>>;
    users: IUser[];
    selectedUser: IUser | null;
    setSelectedUser: Dispatch<SetStateAction<IUser | null>>;
    isLoading: boolean;
    slotList: Slot[];
    setSlot: Dispatch<SetStateAction<string | null>>;
    slot: string | null;
    onDropOff: () => void;
    isDropping: boolean;
    transaction: ExtendedPudoTransaction | null;
    slotMap: Map<string, ClassType>;
    disabledSlots: Slot[];
    spot: Spot | null;
    settings: Setting[];
}

const useOpenDropoffCouriers = (props: UserInterfaceProps): ViewProps => {
    const navigationState = useAppSelector(useSelector, navigationStore);
    const iot = useAppSelector(useSelector, navigationStore).iot;
    const documentRepository = useDocumentRepository();
    const [senders, setSenders] = useState<UserWithDocument[]>([]);
    const [selectedSender, setSelectedSender] = useState<UserWithDocument | null>(null);
    const [users, setUsers] = useState<IUser[]>([]);
    const [selectedUser, setSelectedUser] = useState<IUser | null>(null);
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [slot, setSlot] = useState<string | null>(null);
    const [slotList, setSlotList] = useState<Slot[]>([]);
    const [availableSlots, setAvailableSlots] = useState<Slot[]>([]);
    const [disabledSlots, setDisabledSlots] = useState<Slot[]>([]);
    const [foundSlot, setFoundSlot] = useState<Slot | null>(null);
    const [isDropping, setIsDropping] = useState<boolean>(false);
    const [transaction, setTransaction] = useState<ExtendedPudoTransaction | null>(null);
    const senderGroup = props.userInterface.data.senderGroup;
    const receiverGroup = props.userInterface.data.receiverGroup;
    const [spot, changeSpot] = useState<Spot | null>(null);
    const [module, setModule] = useState<string | null>(null);
    const [settings, setSettings] = useState<Setting[]>([]);

    const slotMap = useMemo(() => {
        const map = new Map<string, ClassType>();
        availableSlots.map((slot) => map.set(slot.id, ClassType.PRIMARY));

        return map;
    }, [availableSlots]);

    useEffect(() => {
        if (!iot) return;
        if (iot.spot_id) {
            SpotClient.fetchSpot(iot.account_id, iot.spot_id).then(changeSpot);
        }
        FunctionalityClient.fetchFunctionalities({ account_id: iot.account_id, feature: Feature.INBOUND_TRANSACTION, target: FeatureTarget.API }).then(
            (res) => {
                if (res.results.length === 0) {
                    return;
                } else {
                    SettingClient.fetchSettings({ entity_id: res.results[0].id, entity_type: EntityType.FUNCTIONALITY, account_id: iot.account_id }).then(
                        (res) => setSettings(res.results)
                    );
                }
            }
        );
    }, [iot]);

    useEffect(() => {
        if (iot) {
            UserGroupClient.fetchUserGroup(iot.account_id, receiverGroup).then((group) => {
                UserGroupClient.fetchUserGroupMembers({ group_id: group.id }).then((members) => {
                    const ids = members.results.map((member) => member.entity_id);
                    UserClient.resolveUsers(ids).then((users) => {
                        setUsers(users.results);
                    });
                });
            });
            UserGroupClient.fetchUserGroup(iot.account_id, senderGroup)
                .then((group) => {
                    UserGroupClient.fetchUserGroupMembers({ group_id: group.id }).then((members) => {
                        const ids = members.results.map((member) => member.entity_id);
                        UserClient.resolveUsers(ids).then((users) => {
                            documentRepository
                                .preLoadFiles(
                                    EntityType.USER,
                                    users.results.map((u) => u.id)
                                )
                                .then(() => {
                                    users.results.forEach((user) => {
                                        documentRepository.resolveFile(EntityType.USER, user.id).then((service) => {
                                            if (service.getDocuments().length > 0) {
                                                service.getUrl(service.getDocuments()[0].id).then((res) => {
                                                    setSenders((prev) => {
                                                        const foundSender = prev.find((s) => s.id === user.id);
                                                        if (foundSender) {
                                                            return prev;
                                                        }
                                                        return [...prev, { ...user, url: res }];
                                                    });
                                                    setIsLoading(false);
                                                });
                                            }
                                            setIsLoading(false);
                                        });
                                    });
                                });
                        });
                    });
                })
                .catch(ErrorHandler.handleError);
            IoTClient.patchIoT(iot.account_id, iot.id, { state: IoTStateName.IOT_OPEN_DROP_OFF_PACKAGE }).then((iot) => {
                props.dispatch(updateIoTState({ iot_state: iot.state }));
            });
            const spotId = navigationState.iot?.spot_id;
            if (!spotId) return;
            SpotClient.fetchSpot(iot.account_id, spotId as string).then((spot) => {
                SlotClient.fetchSlots({ spot_id: spot.id, index: '0', size: '100' }).then((slots) => {
                    SlotWorkflowClient.postAvailableSlots(
                        spot.account_id,
                        spot.id,
                        {
                            account_id: iot.account_id,
                            transaction_type: TransactionType.OPEN_DROP_OFF
                        },
                        { index: '0', size: '100', type: SlotType.DEFAULT }
                    ).then((availableSlots) => {
                        setSlotList(slots.results);
                        const disabledSlots = slots.results.filter((slot) => !availableSlots.results.map((s) => s.id).includes(slot.id));
                        setDisabledSlots(disabledSlots);
                        setAvailableSlots(availableSlots.results);
                    });
                });
            });
        }
    }, []);

    useEffect(() => {
        if (slot) {
            const foundSlot = slotList.find((s) => s.id === slot);
            if (!foundSlot) return;
            setFoundSlot(foundSlot);
            setModule(foundSlot.module_id);
        }
    }, [slot]);

    const onDropOff = async () => {
        if (selectedSender && selectedUser && slot && iot) {
            if (!foundSlot) return;
            const trx = await TransactionPuDoClient.postPudoTransaction(iot.account_id, {
                senders: [selectedSender.id],
                receivers: [selectedUser.id],
                feature: Feature.INBOUND_TRANSACTION,
                slot_id: slot,
                spot_id: spot?.id,
                module_id: module,
                type: TransactionType.PUDO,
                application: Application.PUDO,
                workflow_id: settings.find((s) => s.key === SettingKey.TRANSACTION_WORKFLOW_ID)?.value as string,
                drop_off_package_workflow_id: settings.find((s) => s.key === SettingKey.DROP_OFF_PACKAGE_WORKFLOW_ID)?.value as string,
                pick_up_package_workflow_id: settings.find((s) => s.key === SettingKey.PICK_UP_PACKAGE_WORKFLOW_ID)?.value as string
            });
            setTransaction(trx);

            await TransactionWorkflowClient.triggerTransactionState(trx.account_id, trx.id, {
                trigger: TransactionTriggerName.TRANSACTION_PROCESS,
                location: navigationState.iot?.spot_id ?? undefined,
                actor: navigationState.user?.id ?? undefined
            });
            setIsDropping(true);
        }
    };

    return {
        setSelectedSender,
        senders: senders.sort((a, b) => (a.first_name ?? '').localeCompare(b.first_name ?? '')),
        selectedSender,
        setSelectedUser,
        users,
        selectedUser,
        isLoading,
        slotList,
        setSlot,
        slot,
        onDropOff,
        isDropping,
        transaction,
        slotMap,
        disabledSlots,
        spot,
        settings
    };
};

export default useOpenDropoffCouriers;

interface UserWithDocument extends IUser {
    url: string;
}
